import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Bed, Bath, Square } from 'lucide-react';

// Datos de ejemplo para las propiedades
const properties = [
  {
    id: 1,
    title: 'Villa Moderna con Vista al Mar',
    location: 'Costa del Sol, Málaga',
    price: '1,250,000€',
    image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1771&q=80',
    beds: 4,
    baths: 3,
    sqm: 320,
  },
  {
    id: 2,
    title: 'Ático de Lujo en Centro Urbano',
    location: 'Salamanca, Madrid',
    price: '950,000€',
    image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1770&q=80',
    beds: 3,
    baths: 2,
    sqm: 180,
  },
  {
    id: 3,
    title: 'Chalet Exclusivo con Jardín',
    location: 'La Moraleja, Madrid',
    price: '2,800,000€',
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1775&q=80',
    beds: 5,
    baths: 4,
    sqm: 450,
  },
];

// Componente para cada tarjeta de propiedad
const PropertyCard: React.FC<{
  property: typeof properties[0];
  index: number;
}> = ({ property, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
    >
      <div className="relative h-64 overflow-hidden">
        <img
          src={property.image}
          alt={property.title}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
        />
        <div className="absolute top-4 right-4 bg-secondary text-dark font-medium py-1 px-3 rounded-md">
          {property.price}
        </div>
      </div>
      <div className="p-6">
        <h3 className="text-xl font-serif font-bold text-dark dark:text-light mb-2">
          {property.title}
        </h3>
        <div className="flex items-center text-gray-500 dark:text-gray-400 mb-4">
          <MapPin size={16} className="mr-1" />
          <span className="text-sm">{property.location}</span>
        </div>
        <div className="flex justify-between items-center border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
          <div className="flex items-center text-gray-600 dark:text-gray-300">
            <Bed size={18} className="mr-1" />
            <span className="text-sm mr-3">{property.beds} hab</span>
            <Bath size={18} className="mr-1" />
            <span className="text-sm mr-3">{property.baths} baños</span>
            <Square size={18} className="mr-1" />
            <span className="text-sm">{property.sqm} m²</span>
          </div>
        </div>
        <button className="btn btn-outline w-full mt-4">Ver detalles</button>
      </div>
    </motion.div>
  );
};

const Properties: React.FC = () => {
  return (
    <section id="properties" className="section bg-light dark:bg-dark">
      <div className="container">
        <div className="text-center mb-12">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl md:text-4xl font-serif font-bold text-dark dark:text-light mb-4"
          >
            Propiedades Destacadas
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="w-24 h-1 bg-secondary mx-auto mb-6"
          ></motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto"
          >
            Descubra nuestra selección de propiedades exclusivas, elegidas cuidadosamente para satisfacer los estándares más exigentes.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {properties.map((property, index) => (
            <PropertyCard key={property.id} property={property} index={index} />
          ))}
        </div>

        <div className="text-center mt-12">
          <button className="btn btn-primary">Ver todas las propiedades</button>
        </div>
      </div>
    </section>
  );
};

export default Properties;